import Button from 'tio-ui/components/button';
import Chip from 'tio-ui/components/chip';
import type { ChipSignature } from 'tio-ui/components/chip';
import { dropTask } from 'ember-concurrency';
import { getSingleValueForTasField } from 'tio-common/utils/tuition-assistance/fields';
import { saveCoursesDate } from 'tio-common/utils/tuition-assistance/application-new-route';
import { htmlSafe } from '@ember/template';
import { instanceBadgeState } from 'tio-employee/utils/tas/instanceStateMap';
import { and, not, or } from 'tio-ui/utilities';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import { VStack, HStack, Section } from 'tio-ui/components/layout';
import ActivityCard from 'tio-employee/components/tas/activity-card';
import { amountInfoFromTasApplication } from 'tio-employee/utils/tas/activity-helpers';
import Component from '@glimmer/component';
import CourseCashTag from 'tio-common/components/tas/course-cash-tag';
import lastFourDigits from 'tio-common/helpers/last-four-digits';
import NavTabs from 'tio-ui/components/nav-tabs';
import safeFormatDate from 'tio-common/helpers/safe-format-date';
import StartDate from 'tio-employee/components/tas/start-date-eligibility';
import TASProgramDetailsWithdrawComplete from 'tio-employee/components/tuition-assistance/program-details/withdraw-complete';
import AlertBanner from 'tio-ui/components/alert-banner';
import type { Step } from 'tio-common/components/tio/progress-tracker-items';
import type { TasProgramInstanceState } from 'tio-common/types/tuition-assistance';
import type IntlService from 'ember-intl/services/intl';
import type { NotificationsService } from 'tio-ui/components/notifications';
import type ProgressBuilder from 'tio-common/services/progress-builder';
import type RouterService from '@ember/routing/router';
import type SessionContextService from 'tio-employee/services/session-context';
// This is for types. Anything we have custom may show an error.
import type { Store as StoreService } from '@warp-drive/core';
import { saveRecord } from 'tio-data/requests/save-record';
import { MANAGE_ROUTE } from 'tio-employee/utils/tas/manage-link';
import type { TasApplicationExtension as TasApplicationModel } from 'tio-data/data/resources/tas-application.ext';
import type { TasParticipantExtension as TasParticipantModel } from 'tio-data/data/resources/tas-participant.ext';
import type { TasProgramInstanceExtension as TASProgramInstanceModel } from 'tio-data/data/resources/tas-program-instance.ext';
import type TuitionAssistanceService from 'tio-common/services/tuition-assistance';
import type AuthenticatedTasProgramsShowController from 'tio-employee/controllers/authenticated/tas/programs/show';
import VerticalProgressTracker from 'tio-common/components/tio/vertical-progress-tracker';

interface RouteSignature {
  Args: {
    model: {
      activeInstances: TASProgramInstanceModel[];
      programInstance: TASProgramInstanceModel;
      tasParticipant: TasParticipantModel;
    };
    controller: AuthenticatedTasProgramsShowController;
  };
}

export default class AuthenticatedTasProgramsShow extends Component<RouteSignature> {
  @service declare intl: IntlService;
  @service declare notifications: NotificationsService;
  @service declare progressBuilder: ProgressBuilder;
  @service declare router: RouterService;
  @service declare sessionContext: SessionContextService;
  @service declare store: StoreService;
  @service declare tuitionAssistance: TuitionAssistanceService;

  @tracked isOpen = true;

  get highlightNewApplication() {
    return this.args.controller.fromModal;
  }

  get programTemplate() {
    return this.args.model.programInstance.tasProgramTemplate;
  }

  get programInstanceBadgeState() {
    const approvalRequired = this.programTemplate?.programApprovalRequired;
    return instanceBadgeState(this.args.model.programInstance.state, approvalRequired) as Record<
      string,
      ChipSignature['Args']['intent']
    >;
  }

  get institution() {
    return getSingleValueForTasField(
      'SCHOOL_INSTITUTION_NAME',
      this.args.model.programInstance.fields
    ) as string;
  }

  get isInProgress() {
    return this.args.model.programInstance.programInstanceInProgress;
  }

  get steps() {
    return this.progressBuilder.applicationProcess() as Step[];
  }

  get programProgress() {
    return this.progressBuilder.programProgress(this.args.model.programInstance) as Step[];
  }

  get hasActiveApplications() {
    return !!this.args.model.programInstance.tasApplications.length;
  }

  get isHeldParticipant() {
    return !!this.args.model.tasParticipant?.isHeld;
  }

  get isActiveEligibility() {
    return this.tuitionAssistance.hasActiveEligibilityForProgramTemplate(this.programTemplate);
  }

  get ineligibleBasedOnWaitingPeriod() {
    return this.tuitionAssistance.ineligibleBasedOnWaitingPeriod(
      this.programTemplate?.eligibilityWaitingPeriod,
      this.args.model.tasParticipant
    );
  }

  get isSingleApplicationProgram() {
    return !!getSingleValueForTasField('SINGLE_APPLICATION_PROGRAM', this.programTemplate?.fields);
  }

  applicationName = (application: TasApplicationModel) => {
    return application.displayNameOrSemesterCode as string;
  };

  appAmountInfo = (application: TasApplicationModel) => {
    return amountInfoFromTasApplication(application);
  };

  appNormalizedState = (application: TasApplicationModel) => {
    const approvalRequired = !!this.programTemplate?.programApprovalRequired;
    if (
      application.state === 'TAS.ApplicationState.PENDING_COURSES_APPROVAL' &&
      !approvalRequired
    ) {
      return 'TAS.ApplicationState.COURSES_APPROVED';
    }
    return application.state;
  };

  appRepaymentState = (application: TasApplicationModel) => {
    return application.paymentState === 'TAS.ApplicationPaymentState.REFUND_REQUESTED'
      ? application.paymentState
      : undefined;
  };

  get canCreateApplications() {
    const programApprovalRequired = this.programTemplate?.programApprovalRequired;

    const validStates: TasProgramInstanceState[] = [
      'TAS.ProgramInstanceState.PRIMED',
      'TAS.ProgramInstanceState.PENDING_COMPLETION_APPROVAL',
    ];

    if (!programApprovalRequired) {
      validStates.push(
        'TAS.ProgramInstanceState.PROGRAM_APPROVED',
        'TAS.ProgramInstanceState.PENDING_PROGRAM_APPROVAL'
      );
    }
    return validStates.includes(this.args.model.programInstance.state);
  }

  get programHasExistingApplications() {
    return !!this.args.model.programInstance?.tasApplications?.length;
  }

  get typeClassification() {
    return this.programTemplate.typeClassification;
  }

  get canApplyForApplication() {
    if (
      this.programTemplate.state === 'TAS.ProgramTemplateState.SUSPENDED' ||
      this.typeClassification === 'TAS.ProgramType.1' ||
      this.isHeldParticipant ||
      !this.isActiveEligibility ||
      !this.sessionContext.currentEmployee.id
    ) {
      return false;
    } else if (this.isSingleApplicationProgram) {
      return this.canCreateApplications && !this.programHasExistingApplications;
    }
    return this.canCreateApplications;
  }

  get isPendingApproval() {
    if (!this.programTemplate.programApprovalRequired) {
      return false;
    }
    return (
      this.args.model.programInstance.state === 'TAS.ProgramInstanceState.PENDING_PROGRAM_APPROVAL'
    );
  }

  get showEligibility() {
    const lock = getSingleValueForTasField(
      'LOCK_EMPLOYEE_STATUS_BASED_ON',
      this.programTemplate.fields
    ) as string;
    const hasLock = () => ['COURSE_START_DATE', 'COURSE_END_DATE'].includes(lock);
    return hasLock();
  }

  get programTemplateName() {
    return getSingleValueForTasField('PROGRAM_NAME', this.programTemplate.fields) as string;
  }

  get isProgramLaunched() {
    return this.programTemplate.state === 'TAS.ProgramTemplateState.LAUNCHED';
  }

  get tasApplicationsSorted() {
    const apps = this.args.model.programInstance.tasApplications ?? [];

    const nonAbandoned = apps.filter((a) => a.state !== 'TAS.ApplicationState.ABANDONED');
    const abandoned = apps.filter((a) => a.state === 'TAS.ApplicationState.ABANDONED');

    return [...nonAbandoned, ...abandoned];
  }

  get applicationRoute() {
    const typeMap: Record<string, string> = {
      'TAS.ProgramType.2': 'type-two',
      'TAS.ProgramType.3': 'type-three',
      'TAS.ProgramType.4.2': 'type-four',
      'TAS.ProgramType.4.3': 'type-four',
    };
    const typeSlug = typeMap[this.typeClassification] ?? 'type-four';
    // Guided wizard is the only application flow.
    return `authenticated.tas.applications.${typeSlug}-guided.new`;
  }

  newApplicationAction = () => {
    const date = new Date().toLocaleDateString('en-CA');
    this.createApplicationAndApply.perform(date);
  };

  createApplicationAndApply = dropTask(async (date?: string) => {
    const route = this.applicationRoute;
    try {
      const application = this.store.createRecord<TasApplicationModel>('tas-application', {
        tasProgramInstance: this.args.model.programInstance,
      });
      // Apply the eligibility date to the new record BEFORE its first save so the create
      // request persists it in a single write. Previously the date was applied by the
      // /new route in a second PATCH fired immediately after this create — two writes to
      // the same brand-new row in quick succession, which intermittently lost an
      // optimistic-lock race on the server (StaleObjectError) and blanked the date. The
      // lock status is read here from the already-loaded template (reliable), and the
      // route's own saveCoursesDate then finds the field already set and no-ops.
      if (date) {
        saveCoursesDate(date, application, this.programTemplate.lockEmployeeStatusBasedOn);
      }
      await saveRecord(this.store, application);
      const queryParams: Record<string, unknown> = {};
      if (date) {
        queryParams.date = date;
      }
      this.router.transitionTo(route, application.id, { queryParams });
    } catch (error) {
      console.error('Failed to save application:', error);
      this.notifications.add(this.intl.t('tuition_assistance.errors.apply_failed'), {
        appearance: 'error',
      });
    }
  });

  <template>
    <VStack class="w-full">
      <NavTabs
        @tabs={{array
          (hash label=(t "tas.program.program") route="authenticated.tas.programs.show")
          (hash label=(t "tas.program.details") route="authenticated.tas.programs.details")
          (hash label=(t "tas.program.history") route="authenticated.tas.programs.history")
        }}
      />
      {{#if
        (and
          this.programTemplate.isDirectPayProgram
          this.programTemplate.isSuspended
          this.programTemplate.suspendedProgramDisclosure
        )
      }}
        <AlertBanner
          @intent="warning"
          @title={{htmlSafe (t "tuition_assistance.alerts.coursecash_program_update")}}
        >
          <p class="text-sm">
            {{this.programTemplate.suspendedProgramDisclosure}}
          </p>
        </AlertBanner>
      {{/if}}
      <HStack>
        <Section class="w-full lg:basis-2/3">
          <:header>
            <div class="tio-flex-between flex-row">
              <div class="tio-row-flush">
                <p class="mr-4">{{this.programTemplateName}}</p>
                <Chip @intent={{this.programInstanceBadgeState.intent}}>
                  {{this.programInstanceBadgeState.label}}
                </Chip>
              </div>
            </div>
          </:header>
          <:actions>
            <TASProgramDetailsWithdrawComplete
              @programInstance={{@model.programInstance}}
              @isHeldParticipant={{this.isHeldParticipant}}
            />
          </:actions>
          <:body>
            <VStack>
              <div class="tio-col-extra-tight">
                <p>{{@model.programInstance.institutionName}}</p>
                {{#if @model.programInstance.estimatedProgramBegin}}
                  <p>
                    {{safeFormatDate @model.programInstance.estimatedProgramBegin}}
                    -
                    {{safeFormatDate @model.programInstance.estimatedProgramCompletion}}
                  </p>
                {{else}}
                  <p class="italic">{{t "tas.program.start_date_not_set"}}</p>
                {{/if}}
                <CourseCashTag
                  @isDirectPayProgram={{this.programTemplate.isDirectPayProgram}}
                  @size="sm"
                >
                  <:beforeTag>
                    <span>
                      {{t "common.tas.course_cash.eligible_for"}}
                    </span>
                  </:beforeTag>
                </CourseCashTag>
              </div>
              {{#if this.hasActiveApplications}}
                <VStack @collapsed={{true}}>
                  <Section>
                    <:body>
                      <div class="w-full tio-row-between">
                        <h2 class="text-lg font-medium">
                          {{t "tuition_assistance.applications.default"}}
                        </h2>
                        {{#if this.canApplyForApplication}}
                          <Button
                            @intent="primary"
                            {{on "click" this.newApplicationAction}}
                            @isDisabled={{not this.isProgramLaunched}}
                            class="{{if
                                this.highlightNewApplication
                                'tio-highlight-new-application'
                              }}"
                          >
                            {{t "tas.program.new_application"}}
                          </Button>
                        {{/if}}
                      </div>
                      <div class="tio-col mt-4">
                        {{#each this.tasApplicationsSorted as |application|}}
                          <ActivityCard
                            @route={{MANAGE_ROUTE}}
                            @model={{application.id}}
                            @name={{or
                              (this.applicationName application)
                              (t "tas.application.untitled_application")
                            }}
                            @state={{this.appNormalizedState application}}
                            @shortId={{lastFourDigits application.id}}
                            @updatedAt={{application.updatedAt}}
                            @courseCount={{if
                              application.isCreditBased
                              application.totalCredits
                              application.tasCourses.length
                            }}
                            @isCreditBased={{application.isCreditBased}}
                            @amountInfo={{this.appAmountInfo application}}
                            @paymentState={{this.appRepaymentState application}}
                          />
                        {{/each}}
                      </div>
                    </:body>
                  </Section>
                </VStack>
              {{else if this.isPendingApproval}}
                <VStack @collapsed={{true}}>
                  <Section>
                    <AlertBanner
                      @intent="tip"
                      @title={{t "tas.program.pending_program_approval"}}
                      class="mb-4"
                    >
                      <p class="text-sm tio-fg-brand">
                        {{t "tas.program.pending_program_approval_description"}}
                      </p>
                    </AlertBanner>
                  </Section>
                </VStack>
              {{else if this.canApplyForApplication}}
                <VStack @collapsed={{true}}>
                  <Section>
                    <h2 class="mb-4 text-lg font-medium">{{t "tas.program.apply_for_courses"}}</h2>
                    <p class="mb-4">{{t "tas.program.course_application_description"}}</p>
                    {{#if this.showEligibility}}
                      <StartDate
                        @canApplyToProgram={{and
                          this.canApplyForApplication
                          this.isProgramLaunched
                        }}
                        @buttonText="Get Started"
                        @isMultipleInstanceProgram={{true}}
                        @programTemplate={{this.programTemplate}}
                        @tasParticipant={{@model.tasParticipant}}
                        @applyToProgram={{this.createApplicationAndApply.perform}}
                      />
                    {{else}}
                      <Button
                        @intent="primary"
                        {{on "click" this.newApplicationAction}}
                        class="uppercase w-60
                          {{if this.highlightNewApplication 'tio-highlight-new-application'}}"
                        @isDisabled={{not this.isProgramLaunched}}
                        data-test-program-get-started-button
                      >
                        {{t "login.get_started"}}
                      </Button>
                    {{/if}}
                  </Section>
                  <Section>
                    <h2 class="mb-4 text-lg font-medium">
                      {{t "tas.program.course_application_process"}}
                    </h2>
                    <VerticalProgressTracker class="m-6" @steps={{this.steps}} />
                  </Section>
                </VStack>
              {{/if}}
            </VStack>
          </:body>
        </Section>
        <Section class="w-full lg:basis-1/3">
          <:header>{{t "common.progress_tracker.program_progress"}}</:header>
          <:body>
            {{! TODO: using lists to show numbers causes flow issues. The ml-4 class will force
                the numbers into the box, but this isn't ideal as mobile view will have issues.
                Looks like the component is just a stack anyway so might be target for update. }}
            <VerticalProgressTracker class="ml-4" @steps={{this.programProgress}} />
          </:body>
        </Section>
      </HStack>
    </VStack>
  </template>
}
